import { useContext, useState } from 'react'
import { InformationCircleIcon, TagIcon } from '@heroicons/react/24/outline'
import Compressor from 'compressorjs'
import { Controller, FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { format } from 'libs/dates'
import {
	ColorPicker,
	FileUploader,
	FormSection,
	PriceInput,
	Select,
	Switch
} from 'shared/components'
import { InputGroup } from 'shared/molecules'
import { useSelect, useUserRoles } from 'shared/hooks'
import { StoreSettingsContext } from 'store'
import type { useProductOptions } from 'features/products/hooks/useProductOptions'
import type { ProductFieldGates } from 'features/products/hooks/useProductFieldGates'

type ProductFormSectionsProps = {
	form: UseFormReturn<FieldValues>
	options: ReturnType<typeof useProductOptions>
	gates: ProductFieldGates
	productColor: string
	onColorChange: (data: { hex: string }) => void
	publishedAt?: Date | string | null
}

const optionsOf = (source: { options: { label: string; value: number }[] }) => source.options

export const ProductFormSections = ({
	form,
	options,
	gates,
	productColor,
	onColorChange,
	publishedAt
}: ProductFormSectionsProps) => {
	const { t } = useTranslation()
	const { isStaff } = useUserRoles()
	const storeCtx = useContext(StoreSettingsContext)
	const settings = storeCtx?.storeSettings
	const [images, setImages] = useState<any[]>([])
	const {
		register,
		control,
		setValue,
		trigger,
		watch,
		formState: { errors }
	} = form
	const { handleSelectChange } = useSelect({ setValue, trigger })
	const hasImage = !!watch('image')
	const fieldErrors = errors as any

	const handleImageChange = (data: any) => {
		if (data.length) {
			new Compressor(data[0]?.file, {
				quality: 0.6,
				maxHeight: 800,
				maxWidth: 800,
				success: (compressedResult) => {
					setValue('image', compressedResult)
				}
			})
		} else {
			setValue('image', null)
		}
	}

	const selectChange = (id: string) => (data: any) =>
		handleSelectChange({ id, value: data?.value, label: data?.label })

	const canPublish = settings?.users_can_publish_products || !!isStaff
	const publishBlocked = !!settings?.photos_required_to_publish && !hasImage
	const publishedLabel = publishedAt
		? t('Published on {{ date }}', { date: format(new Date(publishedAt), 'dd.MM.yyyy') })
		: t('Published')

	return (
		<>
			<FormSection
				title={t('Basic information')}
				description={t('Name, booking, quantity and price.')}
				icon={<InformationCircleIcon />}>
				<InputGroup
					id="name"
					className="sm:col-span-2"
					label={t('Product name')}
					placeholder={t('e.g. Blue denim jacket')}
					required
					{...register('name')}
					error={fieldErrors.name}
				/>
				<Controller
					name="booking"
					control={control}
					render={({ field }) => (
						<Select
							id="booking"
							className="sm:col-span-2"
							variant="form"
							placeholder={t('Choose booking')}
							label={t('Booking')}
							required
							options={optionsOf(options.bookings)}
							handleInfiniteScroll={options.bookings.fetchNextPage}
							{...field}
							error={fieldErrors.booking?.value}
							onChange={selectChange('booking')}
						/>
					)}
				/>
				<InputGroup
					id="size"
					label={t('Size')}
					placeholder={t('e.g. M')}
					{...register('size')}
					error={fieldErrors.size}
				/>
				{settings?.quantity_enabled && (
					<InputGroup
						id="quantity"
						type="number"
						label={t('Quantity')}
						required
						{...register('quantity')}
						error={fieldErrors.quantity}
					/>
				)}
				{settings?.cost_price_enabled && (
					<PriceInput
						id="cost_price"
						label={t('Cost price')}
						{...register('cost_price')}
						error={fieldErrors.cost_price}
					/>
				)}
				<PriceInput
					id="price"
					className={settings?.cost_price_enabled ? undefined : 'sm:col-span-2'}
					label={t('Price')}
					placeholder="0"
					required
					{...register('price')}
					error={fieldErrors.price}
				/>
			</FormSection>

			<FormSection
				title={t('Product details')}
				description={t('Category, tags and image.')}
				icon={<TagIcon />}
				tone="success">
				{settings?.categories_enabled && (
					<Controller
						name="category"
						control={control}
						render={({ field }) => (
							<Select
								id="category"
								className="sm:col-span-2"
								variant="form"
								label={t('Category')}
								placeholder={t('Choose category')}
								options={optionsOf(options.categories)}
								handleInfiniteScroll={options.categories.fetchNextPage}
								{...field}
								isLoading={options.categories.isLoading}
								error={fieldErrors.category?.value}
								isSearchable
								onChange={selectChange('category')}
							/>
						)}
					/>
				)}
				{settings?.tags_enabled && (
					<div className="sm:col-span-2">
						<Controller
							name="tags"
							control={control}
							render={({ field }) => (
								<Select
									id="tags"
									variant="form"
									label={t('Tags')}
									placeholder=""
									options={optionsOf(options.tags)}
									handleInfiniteScroll={options.tags.fetchNextPage}
									isLoading={options.tags.isLoading}
									{...field}
									isSearchable
									isMulti
								/>
							)}
						/>
						<p className="mt-1.5 text-xs text-muted-foreground">
							{t('A comma-separated list of tags.')}
						</p>
					</div>
				)}
				{settings?.product_fabrics_enabled && (
					<Controller
						name="productFabricType"
						control={control}
						render={({ field }) => (
							<Select
								id="productFabricType"
								variant="form"
								label={t('Fabric')}
								placeholder={t('All')}
								options={optionsOf(options.fabrics)}
								handleInfiniteScroll={options.fabrics.fetchNextPage}
								{...field}
								isLoading={options.fabrics.isLoading}
								error={fieldErrors.productFabricType?.value}
								onChange={selectChange('productFabricType')}
							/>
						)}
					/>
				)}
				{settings?.product_conditions_enabled && (
					<Controller
						name="productCondition"
						control={control}
						render={({ field }) => (
							<Select
								id="productCondition"
								variant="form"
								label={t('Condition')}
								placeholder={t('All')}
								options={optionsOf(options.conditions)}
								handleInfiniteScroll={options.conditions.fetchNextPage}
								{...field}
								isLoading={options.conditions.isLoading}
								error={fieldErrors.productCondition?.value}
								onChange={selectChange('productCondition')}
							/>
						)}
					/>
				)}
				{settings?.product_genders_enabled && (
					<Controller
						name="productGender"
						control={control}
						render={({ field }) => (
							<Select
								id="productGender"
								variant="form"
								label={t('Gender')}
								placeholder={t('All')}
								options={optionsOf(options.genders)}
								handleInfiniteScroll={options.genders.fetchNextPage}
								{...field}
								isLoading={options.genders.isLoading}
								error={fieldErrors.productGender?.value}
								onChange={selectChange('productGender')}
							/>
						)}
					/>
				)}
				{settings?.product_types_enabled && (
					<Controller
						name="productType"
						control={control}
						render={({ field }) => (
							<Select
								id="productType"
								variant="form"
								label={t('Type')}
								placeholder={t('All')}
								options={optionsOf(options.types)}
								handleInfiniteScroll={options.types.fetchNextPage}
								{...field}
								isLoading={options.types.isLoading}
								error={fieldErrors.productType?.value}
								onChange={selectChange('productType')}
							/>
						)}
					/>
				)}
				{gates.supplierActive && (
					<>
						<InputGroup
							id="supplier_barcode"
							label={t('Supplier barcode')}
							{...register('supplier_barcode')}
							error={fieldErrors.supplier_barcode}
						/>
						<InputGroup
							id="supplier_sku"
							label={t('Supplier SKU')}
							{...register('supplier_sku')}
							error={fieldErrors.supplier_sku}
						/>
					</>
				)}
				{gates.vatActive && (
					<Controller
						name="vat_rate"
						control={control}
						render={({ field }) => (
							<Select
								id="vat_rate"
								className="sm:col-span-2"
								variant="form"
								placeholder={t('All')}
								label={t('VAT')}
								options={(settings?.vat_rate_choices ?? []).map(
									({ value, label }: { value: any; label: any }) => ({ value, label: t(label) })
								)}
								{...field}
								error={fieldErrors.vat_rate}
								onChange={selectChange('vat_rate')}
							/>
						)}
					/>
				)}
				<div className="sm:col-span-2">
					<Controller
						name="image"
						control={control}
						render={({ field }) => (
							<FileUploader
								{...field}
								label={t('Image')}
								images={images}
								setImages={setImages}
								onChange={handleImageChange}
							/>
						)}
					/>
				</div>
				{settings?.color_enabled && (
					<div className="sm:col-span-2">
						<ColorPicker label={t('Color')} color={productColor} onChange={onColorChange} />
					</div>
				)}
			</FormSection>

			{canPublish && (
				<div className="rounded-lg border border-border bg-card p-5 shadow-panel">
					<Switch
						label={publishedLabel}
						description={
							settings?.photos_required_to_publish
								? t(
										'A photo needs to be added to the product before it can be published. Products that are published are also available online'
									)
								: t('Products that are published are also available online')
						}
						{...register('published')}
						disabled={publishBlocked}
					/>
				</div>
			)}
		</>
	)
}
